import type { NationalHero } from "@homeinstead/schemas";

/**
 * Extract the variant type from the Sanity-generated schema.
 * This ensures the type stays in sync with the CMS configuration.
 */
export type HeroVariant = NonNullable<NationalHero["variant"]>;

export const HERO_VARIANT_STYLES: Record<
  HeroVariant,
  {
    text: string;
    textPosition?: string;
    helperText: string;
    richTextColor?: "white";
    ratingStarsColor: "default" | "white";
    error: string;
    recipientButton: "primary" | "secondary";
    continueButton: "primary" | "outline-light";
    link: "secondary" | "outline-dark";
  }
> = {
  default: {
    continueButton: "primary",
    error: "text-error",
    helperText: "text-gray-600",
    link: "secondary",
    ratingStarsColor: "default",
    recipientButton: "primary",
    text: "text-new-beginnings-base",
    textPosition: undefined,
  },
  light: {
    continueButton: "outline-light",
    error: "text-error-light",
    helperText: "text-white/80",
    link: "outline-dark",
    ratingStarsColor: "white",
    recipientButton: "secondary",
    richTextColor: "white",
    text: "text-white",
    textPosition: "text-center",
  },
} as const; /**
 * Helper function to get variant styles for hero components.
 * Use this instead of duplicating variant logic across components.
 *
 * @param variant - The hero variant ("default" or "light")
 * @returns Object with textColorClass, helperTextColorClass, richTextColor, ratingStarsColor, errorColorClass, recipientButtonVariant, continueButtonVariant, linkVariant, and isLight flag
 *
 * @example
 * const { textColorClass, errorColorClass, recipientButtonVariant, continueButtonVariant, linkVariant } = getHeroVariantStyles(variant);
 */
export function getHeroVariantStyles(variant: HeroVariant = "default") {
  const styles = HERO_VARIANT_STYLES[variant] || HERO_VARIANT_STYLES.default;
  return {
    continueButtonVariant: styles.continueButton,
    errorColorClass: styles.error,
    helperTextColorClass: styles.helperText,
    isLight: variant === "light",
    linkVariant: styles.link,
    ratingStarsColor: styles.ratingStarsColor,
    recipientButtonVariant: styles.recipientButton,
    richTextColor: styles.richTextColor,
    textColorClass: styles.text,
    textPositionClass: styles.textPosition,
  };
}
